"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import type { SessionListItem, FolderResponse } from "@/app/lib/types";
import SessionItem from "./SessionItem";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FolderItemProps {
  folder: FolderResponse;
  sessions: SessionListItem[];
  isExpanded: boolean;
  onToggle: () => void;
  activeSessionId?: string;
  onSessionClick: (id: string) => void;
  onSessionDelete: (id: string) => void;
  onSessionRename: (id: string, title: string) => void;
  onSessionFlag: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  onMoveSessionToFolder?: (sessionId: string, folderId: string) => void;
  onMoveToNewFolder?: (sessionId: string) => void;
  folders?: FolderResponse[];
  onShare?: (id: string) => void;
  onNewChatInFolder: (folderId: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onSetFolderSeparator?: (id: string, value: boolean) => void;
  onDeleteFolder: (id: string) => void;
  currentUserEmail?: string;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  /** When true, this folder was just created — open it directly in the
   *  (text-selected) rename input so the user can type over the default name. */
  autoEdit?: boolean;
  onAutoEditConsumed?: () => void;
}

export default function FolderItem({
  folder,
  sessions,
  isExpanded,
  onToggle,
  activeSessionId,
  onSessionClick,
  onSessionDelete,
  onSessionRename,
  onSessionFlag,
  onMoveToFolder,
  onMoveSessionToFolder,
  onMoveToNewFolder,
  folders,
  onShare,
  onNewChatInFolder,
  onRenameFolder,
  onSetFolderSeparator,
  onDeleteFolder,
  currentUserEmail,
  selectionMode,
  selectedIds,
  onToggleSelect,
  autoEdit,
  onAutoEditConsumed,
}: FolderItemProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: folder.id });

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  // Freshly-created folder: open straight into the rename input (name selected
  // so the user can type over it). One-shot — clear the parent flag immediately.
  useEffect(() => {
    if (autoEdit) {
      setIsRenaming(true);
      onAutoEditConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRenameSubmit = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== folder.name) {
      onRenameFolder(folder.id, trimmed);
    }
    setIsRenaming(false);
    setShowMenu(false);
  }, [renameValue, folder.id, folder.name, onRenameFolder]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowMenu(true);
  }, []);

  const sortStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div className="folder-item" ref={setNodeRef} style={sortStyle}>
      {/* Folder header */}
      <div
        className="folder-header"
        onClick={selectionMode && onToggleSelect ? () => onToggleSelect(folder.id) : onToggle}
        onContextMenu={handleContextMenu}
        style={{ position: "relative" }}
      >
        {/* Selection checkbox */}
        {selectionMode && (
          <input
            type="checkbox"
            checked={selectedIds?.has(folder.id) || false}
            onChange={() => onToggleSelect?.(folder.id)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 12, height: 12, flexShrink: 0, cursor: "pointer", accentColor: "var(--accent)" }}
          />
        )}
        {/* Chevron */}
        <span
          style={{
            fontSize: "10px",
            color: "var(--text-secondary)",
            width: "16px",
            textAlign: "center",
            flexShrink: 0,
            transition: "transform var(--transition-fast)",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            display: "inline-block",
          }}
        >
          &#9654;
        </span>

        {/* Folder name */}
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameSubmit();
              if (e.key === "Escape") {
                setRenameValue(folder.name);
                setIsRenaming(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              padding: "2px 6px",
              fontSize: "13px",
              background: "var(--surface-hover)",
              border: "1px solid var(--accent)",
              borderRadius: "4px",
              color: "var(--text-primary)",
              outline: "none",
            }}
          />
        ) : (
          <span
            style={{
              flex: 1,
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {folder.name}
          </span>
        )}

        {/* Session count badge */}
        <span
          style={{
            fontSize: "11px",
            color: "var(--text-tertiary)",
            background: "var(--surface-hover)",
            padding: "1px 6px",
            borderRadius: "999px",
            flexShrink: 0,
          }}
        >
          {sessions.length}
        </span>

        {/* New chat in folder button — pencil icon matching bottom toolbar */}
        <button
          className="btn-ghost"
          onClick={(e) => {
            e.stopPropagation();
            onNewChatInFolder(folder.id);
          }}
          style={{ padding: "2px 4px", flexShrink: 0 }}
          aria-label="New chat in folder"
          title="New chat in folder"
        >
          <svg width="13" height="13" viewBox="0 0 18 18" fill="none" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="5" width="10.5" height="10.5" rx="2" />
            <path d="M16 2L8 10" />
          </svg>
        </button>

        {/* Drag handle */}
        <div
          className="folder-reorder-arrows"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          style={{ flexShrink: 0, opacity: 0, cursor: "grab", padding: "2px", touchAction: "none" }}
        >
          <svg width="8" height="16" viewBox="0 0 8 16" fill="var(--text-tertiary)">
            <circle cx="2" cy="2.5" r="1.2" />
            <circle cx="6" cy="2.5" r="1.2" />
            <circle cx="2" cy="8" r="1.2" />
            <circle cx="6" cy="8" r="1.2" />
            <circle cx="2" cy="13.5" r="1.2" />
            <circle cx="6" cy="13.5" r="1.2" />
          </svg>
        </div>

        {/* Context menu */}
        {showMenu && (
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              right: "8px",
              top: "100%",
              zIndex: 20,
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              padding: "4px",
              minWidth: "120px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          >
            <button
              className="btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                setIsRenaming(true);
                setShowMenu(false);
              }}
              style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
            >
              Rename
            </button>
            {onSetFolderSeparator && (
              <button
                className="btn-ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetFolderSeparator(folder.id, !folder.separator_before);
                  setShowMenu(false);
                }}
                style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
              >
                {folder.separator_before ? "Remove separator" : "Add separator before"}
              </button>
            )}
            <button
              className="btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteFolder(folder.id);
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "6px 10px",
                color: "var(--error)",
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Child sessions */}
      {isExpanded && (
        <div style={{ paddingLeft: "16px" }}>
          {sessions.length === 0 ? (
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-tertiary)",
                padding: "8px 12px",
              }}
            >
              No conversations
            </div>
          ) : (
            sessions.map((session) => (
              <SessionItem
                key={session.id}
                session={session}
                isActive={activeSessionId === session.id}
                onClick={() => onSessionClick(session.id)}
                onDelete={onSessionDelete}
                onRename={onSessionRename}
                onFlag={onSessionFlag}
                onMoveToFolder={onMoveToFolder}
                onMoveSessionToFolder={onMoveSessionToFolder}
                onMoveToNewFolder={onMoveToNewFolder}
                folders={folders}
                onShare={onShare}
                currentUserEmail={currentUserEmail}
                selectionMode={selectionMode}
                selected={selectedIds?.has(session.id)}
                onToggleSelect={onToggleSelect}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
